import { useState } from "react"
import { Head, router } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { PageHeader } from "@/components/ui/headers"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { StaffLayout } from "@/components/layouts/staff-layout"
import { t } from "@/lib/i18n"

/**
 * Opening a second group.
 *
 * **The billing consequence is on the screen, not in a doc.** Menny sells B2B
 * and a group is a customer: this one starts with no subscription, and
 * `Location#seated?` will refuse to launch any restaurant filed under it until
 * somebody pays. An operator who reads "create group" and gets a second invoice
 * was not told; one who reads the note was.
 *
 * Only the name is asked for. The fiscal profile lives on the group's settings
 * screen, which is where it can also be corrected — asking for an RFC here
 * would make the two paths into a group disagree, since the one created
 * alongside a first restaurant is never asked for one.
 */

type Props = {
  errors?: Record<string, string> | null
}

const tNew = (key: string) => t(`ui.react.pages.staff.organizations.new.${key}`)

export default function StaffOrganizationsNew({ errors }: Props) {
  const [name, setName] = useState("")
  const title = tNew("page_title")

  return (
    <>
      <Head title={title} />

      <MainContainer size="sm">
        <PageHeader title={title} description={tNew("description")} />

        {/* No card header: `PageHeader` above already names this screen, and so
            does the switcher while you are on it. */}
        <Card density="compact">
          <CardContent className="pt-0">
            <form
              className="space-y-4"
              data-testid="organization-create-form"
              onSubmit={(event) => {
                event.preventDefault()
                router.post("/organizations", { name })
              }}
            >
              <div className="flex flex-col gap-1">
                <Label htmlFor="organization-name" className="text-muted-foreground text-xs">
                  {tNew("name_label")}
                </Label>
                <Input
                  id="organization-name"
                  value={name}
                  required
                  placeholder={tNew("name_placeholder")}
                  aria-invalid={errors?.name ? true : undefined}
                  data-testid="organization-name"
                  onChange={(event) => setName(event.target.value)}
                />
                {errors?.name && (
                  <p className="text-destructive text-xs" data-testid="organization-name-error">
                    {errors.name}
                  </p>
                )}
              </div>

              <p className="text-muted-foreground text-xs" data-testid="organization-billing-note">
                {tNew("billing_note")}
              </p>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  className="h-11 md:h-9"
                  disabled={name.trim() === ""}
                  data-testid="organization-create-submit"
                >
                  {tNew("submit")}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </MainContainer>
    </>
  )
}

StaffOrganizationsNew.layout = (page: React.ReactNode) => <StaffLayout>{page}</StaffLayout>
